import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { ApplyFormFieldValues } from 'pages/Apply/context';

import { getTimeAtAddress } from './helpers';

export interface AddressesInformationFieldsHook {
  previousAddress1Visible: boolean;
  previousAddress2Visible: boolean;
}

export default function useAddressesInformationFields(): AddressesInformationFieldsHook {
  const values = useWatch<ApplyFormFieldValues, 'credit.customer_details'>({
    name: 'credit.customer_details',
  });
  const { unregister } = useFormContext<ApplyFormFieldValues>();

  const timeAtCurrentAddress = useMemo(
    () => getTimeAtAddress(values?.current_address),
    [values],
  );

  const timeAtPreviousAddressOne = useMemo(
    () => getTimeAtAddress(values?.previous_address_one),
    [values],
  );

  const previousAddress1Visible = useMemo(() => {
    if (!timeAtCurrentAddress) return false;
    return timeAtCurrentAddress < 36;
  }, [timeAtCurrentAddress]);

  const previousAddress2Visible = useMemo(() => {
    if (!timeAtPreviousAddressOne || !previousAddress1Visible) return false;
    return timeAtPreviousAddressOne < 36;
  }, [timeAtPreviousAddressOne, previousAddress1Visible]);

  useEffect(() => {
    if (previousAddress1Visible) return;
    unregister('credit.customer_details.previous_address_one');
  }, [unregister, previousAddress1Visible]);

  useEffect(() => {
    if (previousAddress2Visible) return;
    unregister('credit.customer_details.previous_address_two');
  }, [unregister, previousAddress2Visible]);

  return {
    previousAddress1Visible,
    previousAddress2Visible,
  };
}
